import React, { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import type { SurveyNoteCard } from '@officehub/shared';
import { NoteCard } from './NoteCard';
import type { RenderNoteEditor } from './types';
import { newClientId } from '../ids/newClientId';

export interface NoteCardListProps {
  cards: SurveyNoteCard[];
  onChange: (cards: SurveyNoteCard[]) => void;
  /** Injected editor (see NoteCard.renderEditor). Optional. */
  renderEditor?: RenderNoteEditor;
  /** Commit a note when its editor loses focus (see NoteCard.commitOnBlur). */
  commitOnBlur?: boolean;
  /** Extra classes for the list container. */
  className?: string;
}

function buildNewCard(order: number): SurveyNoteCard {
  const now = new Date().toISOString();
  return {
    cardId: newClientId(),
    order,
    text: '',
    createdAt: now,
    updatedAt: now,
  };
}

function reorder(cards: SurveyNoteCard[], fromId: string, toId: string): SurveyNoteCard[] {
  const from = cards.findIndex((c) => c.cardId === fromId);
  const to = cards.findIndex((c) => c.cardId === toId);
  if (from === -1 || to === -1 || from === to) return cards;
  const next = cards.filter((c) => c.cardId !== fromId);
  next.splice(to, 0, cards[from]);
  return next.map((c, i) => ({ ...c, order: i }));
}

export function NoteCardList({
  cards,
  onChange,
  renderEditor,
  commitOnBlur,
  className,
}: NoteCardListProps): React.ReactElement {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overCardId, setOverCardId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const newCardIdRef = useRef<string | null>(null);

  // `updated` is an array because a save whose text exceeds the server's
  // per-card length limit splits into multiple sibling cards instead of
  // being rejected (see NoteCard.saveEdit) -- the common case is a
  // single-element array, which behaves exactly like the old 1:1 replace.
  const handleUpdate = (updated: SurveyNoteCard[]) => {
    if (updated.length === 0) return;
    const idx = cards.findIndex((c) => c.cardId === updated[0].cardId);
    const spliced = idx === -1
      ? [...cards, ...updated]
      : [...cards.slice(0, idx), ...updated, ...cards.slice(idx + 1)];
    onChange(spliced.map((c, i) => ({ ...c, order: i })));
    if (updated.some((c) => c.cardId === newCardIdRef.current)) {
      newCardIdRef.current = null;
      setAddingNew(false);
    }
  };

  const handleDelete = (cardId: string) => {
    const next = cards.filter((c) => c.cardId !== cardId).map((c, i) => ({ ...c, order: i }));
    onChange(next);
    if (cardId === newCardIdRef.current) {
      newCardIdRef.current = null;
      setAddingNew(false);
    }
  };

  const handleAddCard = () => {
    const card = buildNewCard(cards.length);
    newCardIdRef.current = card.cardId;
    setAddingNew(true);
    onChange([...cards, card]);
  };

  const handleDragStart = (e: React.DragEvent, cardId: string) => {
    setDraggingId(cardId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, cardId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverCardId(cardId);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (draggingId && draggingId !== targetId) {
      onChange(reorder(cards, draggingId, targetId));
    }
    setDraggingId(null);
    setOverCardId(null);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setOverCardId(null);
  };

  const sorted = [...cards].sort((a, b) => a.order - b.order);

  return (
    <div className={`flex flex-col gap-2 ${className ?? ''}`} onDragEnd={handleDragEnd}>
      {sorted.map((card) => (
        <NoteCard
          key={card.cardId}
          card={card}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          isDraggingOver={overCardId === card.cardId && draggingId !== card.cardId}
          renderEditor={renderEditor}
          commitOnBlur={commitOnBlur}
        />
      ))}

      {!addingNew && (
        <button
          type="button"
          onClick={handleAddCard}
          className="flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 py-3 text-sm font-medium text-slate-500 hover:border-[#007ACC] hover:text-[#007ACC]"
        >
          <Plus size={16} />
          Add note
        </button>
      )}
    </div>
  );
}
